import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/**
 * Which side of the dashboard someone was on — Wosool, Fosool «فصول» or
 * Osool «أصول» — and the page they last had open on each, so the product
 * rail returns them there.
 *
 * Only the shared pages read the side (the URL decides everywhere else; see
 * `utils/productSwitch.js`), and it belongs to one person: school offices
 * share machines, so `rememberedFor` ignores what someone else left behind,
 * and the first write by a new person starts them afresh.
 */
export type ProductSide = "wosool" | "fosool" | "osool";

interface ProductState {
  /** Whose side and pages these are. */
  userId: string | null;
  side: ProductSide;
  lastPath: Partial<Record<ProductSide, string>>;
  /** Remember the side this person is on. */
  rememberSide: (userId: string, side: ProductSide) => void;
  /** Remember the page (path and query) this person has open on a side. */
  rememberPath: (userId: string, side: ProductSide, path: string) => void;
}

export const useProductStore = create<ProductState>()(
  persist(
    (set, get) => ({
      userId: null,
      side: "wosool",
      lastPath: {},

      rememberSide: (userId, side) => {
        const state = get();
        if (state.userId !== userId) {
          set({ userId, side, lastPath: {} });
        } else if (state.side !== side) {
          set({ side });
        }
      },

      rememberPath: (userId, side, path) => {
        const state = get();
        if (state.userId !== userId) {
          set({ userId, side: "wosool", lastPath: { [side]: path } });
        } else if (state.lastPath[side] !== path) {
          set({ lastPath: { ...state.lastPath, [side]: path } });
        }
      },
    }),
    {
      name: "manager-product",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        userId: state.userId,
        side: state.side,
        lastPath: state.lastPath,
      }),
    }
  )
);
